import type { z } from 'zod';

const configuredBase = import.meta.env.VITE_ORCA_API_BASE_URL?.trim();

/**
 * One API origin for auth and every real ORCA endpoint. In local Vite mode an
 * empty base uses the /api proxy; production falls back to the approved host.
 */
export const API_BASE_URL = (configuredBase ?? (import.meta.env.DEV ? '' : 'https://api.theorca.pro')).replace(/\/$/, '');

export type ApiErrorKind = 'api' | 'offline' | 'invalid_response';

export class HttpError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly requestId: string | null,
    readonly kind: ApiErrorKind = 'api',
  ) {
    super(code);
    this.name = 'HttpError';
  }
}

type RequestOptions<T extends z.ZodTypeAny> = {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  csrf?: string;
  notifyAuthExpired?: boolean;
  schema: T;
};

export const AUTH_EXPIRED_EVENT = 'orca:auth-expired';

function apiUrl(path: string) {
  return `${API_BASE_URL}${path}`;
}

/**
 * Credentials are always cookie-based. No session/account credential is read
 * from or written to browser storage by this client.
 */
export async function requestJson<T extends z.ZodTypeAny>(
  path: string,
  { method = 'GET', body, csrf, notifyAuthExpired = true, schema }: RequestOptions<T>,
): Promise<z.infer<T>> {
  let response: Response;
  try {
    response = await fetch(apiUrl(path), {
      method,
      credentials: 'include',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new HttpError(0, 'offline', null, 'offline');
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new HttpError(response.status, 'invalid_response', response.headers.get('X-Request-ID'), 'invalid_response');
  }

  if (!response.ok) {
    const error = payload && typeof payload === 'object' && 'error' in payload
      ? (payload as { error?: { code?: unknown; request_id?: unknown } }).error
      : undefined;
    const code = typeof error?.code === 'string' ? error.code : 'request_failed';
    const requestId = typeof error?.request_id === 'string'
      ? error.request_id
      : response.headers.get('X-Request-ID');
    const next = new HttpError(response.status, code, requestId);
    if (notifyAuthExpired && code === 'unauthenticated' && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
    }
    throw next;
  }

  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    throw new HttpError(response.status, 'invalid_response', response.headers.get('X-Request-ID'), 'invalid_response');
  }
  return parsed.data;
}
